import { Component, Input } from '@angular/core';
import { CiMetric, CiReport } from '../../ci-report';

@Component({
  selector: 'app-property-intelligence',
  standalone: true,
  templateUrl: './property-intelligence.component.html',
  styleUrl: './property-intelligence.component.scss'
})
export class PropertyIntelligenceComponent {
  @Input() report: CiReport | null = null;
  @Input() loading = false;
  @Input() error = '';

  value(metrics: CiMetric[], label: string): string {
    return metrics.find(metric => metric.label === label)?.value ?? 'Not available';
  }

  get tags(): string[] {
    return [...new Set(this.report?.tags ?? [])];
  }

  get pricing(): CiMetric[] {
    return (this.report?.pricing ?? []).filter(metric => !['Revised Benchmark PSF', 'UDS Current Rate PSF'].includes(metric.label));
  }

  // Older templates can remain mounted during Angular hot reload.
  // Keep their removed indicators empty until the updated template is loaded.
  get strengths(): never[] {
    return [];
  }

  get rentalMetrics(): CiMetric[] {
    return (this.report?.valuation ?? []).filter(metric => ['Monthly Rent', 'Annual Rent', 'Rental Yield', 'Rental Strength'].includes(metric.label));
  }

  get udsMetrics(): CiMetric[] {
    const area = this.report?.analysis?.uds;
    const rate = this.value(this.report?.pricing ?? [], 'UDS Current Rate PSF');
    return [
      ...(area == null ? [] : [{ label: 'UDS Area', value: `${area.toLocaleString('en-IN')} sq.ft.` }]),
      ...(rate === 'Not available' ? [] : [{ label: 'UDS Rate', value: rate }]),
      ...(this.report?.valuation ?? []).filter(metric => ['Adjusted Current Value (UDS)', 'UDS Support vs Close High'].includes(metric.label)),
    ];
  }

  get valuation(): CiMetric[] {
    return (this.report?.valuation ?? []).filter(metric => !['Monthly Rent', 'Annual Rent', 'Rental Yield', 'Rental Strength', 'Adjusted Current Value (UDS)', 'UDS Support vs Close High'].includes(metric.label));
  }

  get psfOffers(): CiMetric[] {
    return (this.report?.offers ?? []).filter(metric => metric.label.includes('PSF'));
  }

  get valueOffers(): CiMetric[] {
    return (this.report?.offers ?? []).filter(metric => !metric.label.includes('PSF'));
  }

  get valuationTitle(): string {
    if (this.report?.propertyType === 'Plots') return 'Land Valuation';
    if (this.report?.propertyType === 'Independent House') return 'Land & Building Valuation';
    return 'Valuation & Rental';
  }

  get evidence(): CiMetric[] {
    return (this.report?.pricing ?? []).filter(metric => ['Average PSF of Similar Properties', 'Benchmark PSF', 'Last Transacted PSF', 'Last 5 Transactions Avg PSF', 'Last Transacted Date', 'Ask vs Benchmark', 'Land Rate PSF'].includes(metric.label));
  }

  get hasTransactionHistory(): boolean {
    return !this.isLandProperty && this.report?.propertyType !== 'Primary' && !!this.report?.transactions?.length && this.report.directTransactionHistoryLimited !== true;
  }

  get usesPrimaryComparables(): boolean {
    return !!this.report && !this.isLandProperty && !this.hasTransactionHistory;
  }

  get primaryAnalysisNote(): string {
    if (!this.usesPrimaryComparables || !this.report?.comparableProjects?.length) return '';
    const report = this.report;
    const benchmark = this.value(report.pricing, 'Benchmark PSF');
    const offer = this.value(report.offers, 'Suggested Offer Value');
    const gapText = this.value(report.pricing, 'Ask vs Benchmark');
    const gap = report.analysis?.difference != null ? report.analysis.difference * 100 : Number(gapText.match(/[-+]?\d+(?:\.\d+)?/)?.[0]);
    if (!report.propertyName || !report.microMarket || benchmark === 'Not available' || offer === 'Not available' || !Number.isFinite(gap)) return '';
    const below = gap < 0 || /below/i.test(gapText);
    const comparison = gap === 0 ? 'in line with' : `${Math.abs(gap).toFixed(1)}% ${below ? 'below' : 'above'}`;
    const assessment = gap === 0 ? 'an asking rate in line with the Circled benchmark' : `an asking rate ${below ? 'below' : 'above'} the Circled benchmark`;
    return `Direct transaction history for ${report.propertyName} is limited, so Circled has referenced similar projects within ${report.microMarket} to establish a relevant market benchmark. Based on comparable project evidence and property-specific factors, the benchmark is estimated at ${benchmark}/sq.ft. The current asking rate is ${comparison} this benchmark, indicating ${assessment} with a suggested offer of approximately ${offer}.`;
  }

  get usesResaleTransactions(): boolean {
    return this.hasTransactionHistory;
  }

  get resaleAnalysisNote(): string {
    if (!this.usesResaleTransactions || !this.report) return '';
    const report = this.report;
    const latest = report.transactions?.[0]?.psf ?? this.value(report.pricing, 'Last Transacted PSF');
    const benchmark = this.value(report.pricing, 'Benchmark PSF');
    const offer = this.value(report.offers, 'Suggested Offer Value');
    const gapText = this.value(report.pricing, 'Ask vs Benchmark');
    const gap = report.analysis?.difference != null ? report.analysis.difference * 100 : Number(gapText.match(/[-+]?\d+(?:\.\d+)?/)?.[0]);
    if (!report.propertyName || latest === 'Not available' || benchmark === 'Not available' || offer === 'Not available' || !Number.isFinite(gap)) return '';
    const below = gap < 0 || /below/i.test(gapText);
    const comparison = gap === 0 ? 'in line with' : `${Math.abs(gap).toFixed(1)}% ${below ? 'below' : 'above'}`;
    const assessment = gap === 0 ? 'an asking rate in line with the Circled benchmark' : `an asking rate ${below ? 'below' : 'above'} the Circled benchmark`;
    return `Recent registered transactions within ${report.propertyName} provide direct pricing evidence for this assessment. Based on the latest transaction rate of ${latest}/sq.ft., recent transaction trends and property-specific factors, Circled estimates a benchmark of ${benchmark}/sq.ft. The current ask is ${comparison} the benchmark, indicating ${assessment} with a suggested offer of approximately ${offer}.`;
  }

  get isLandProperty(): boolean {
    return this.report?.propertyType === 'Plots' || this.report?.propertyType === 'Independent House';
  }

  get analystNote(): string {
    if (this.report?.analystNote) {
      // Public notes can also contain the calculator's duplicated adjustment list.
      return this.report.analystNote
        .replace(/Applied adjustments?:[\s\S]*?(?:\.(?=\s|$)|$)/gi, '')
        .replace(/(?:Location|Builder|Legal Status|Scarcity)\s*:\s*[+-]?\d+(?:\.\d+)?%\s*(?:\([^)]*\))?\s*[;,.]?/gi, '')
        .replace(/\s+/g, ' ').trim();
    }
    if (this.isLandProperty) {
      const metrics = this.report?.assessmentMetrics ?? [];
      const land = this.value(metrics, 'Assessed Land Value');
      const total = this.value(metrics, 'Total Assessed Property Value');
      const building = this.value(metrics, 'Building Value');
      const asking = this.compact(this.report?.analysis?.askingPrice);
      const parts = [];
      if (land !== 'Not available') parts.push(`The assessed land value is ${land}.`);
      if (building !== 'Not available') parts.push(`The building value is ${building}.`);
      if (total !== 'Not available') parts.push(`The combined property assessment is ${total}.`);
      if (this.report?.adjustmentBreakdown?.rows.length) parts.push('The applied land adjustments are detailed below.');
      if (asking !== 'Not available' && parts.length) parts.push(`The current asking price is ${asking}.`);
      return parts.join(' ');
    }
    const note = this.primaryAnalysisNote || this.resaleAnalysisNote;
    const asking = this.compact(this.report?.analysis?.askingPrice);
    const facts = [note];
    if (asking !== 'Not available') facts.push(`The current asking price is ${asking}.`);
    return facts.filter(Boolean).join(' ');
  }

  get benchmarkContext(): string {
    if (this.isLandProperty) return this.report?.propertyType === 'Independent House'
      ? 'This assessment considers the plot and the building separately. The asking price is compared with the combined assessed property value supplied in the report.'
      : 'This assessment uses the plot area, the land rate and the applicable land adjustments. The asking price is compared with the assessed land value supplied in the report.';
    if (this.usesPrimaryComparables) return this.report?.comparableProjects?.length
      ? 'Direct project-level history is limited. The comparable project references shown in this report provide context for the Circled benchmark and your offer guidance.'
      : 'Direct project-level history is limited. The report does not include the comparable project details; confirm these references before making an offer.';
    if (this.hasTransactionHistory) {
      return 'The report includes past sale prices. These help you compare the asking price with what buyers have paid. The CI benchmark gives you a reference for discussing price; it may differ from individual sales.';
    }
    const landRate = (this.report?.pricing ?? []).some(metric => metric.label === 'Land Rate PSF');
    const methodology = this.report?.benchmarkExplanation?.methodology ?? '';
    if (!landRate && /comparable|nearby propert/i.test(methodology)) {
      return 'Direct transaction history is not available in this report. The report identifies comparable properties as the price reference. Similar properties help put the asking price in context; the benchmark is a guide for negotiation, not confirmation of a completed sale.';
    }
    return landRate
      ? 'Direct transaction history is not available in this report. The available land-rate reference is shown below to help you understand land value. The CI benchmark gives you a guide for discussing price, rather than evidence of a completed sale.'
      : 'Direct transaction history is not available in this report. The supplied CI benchmark is shown as a guide for comparing the asking price. The report does not identify the supporting sales or reference sources, so those details should be confirmed before you make an offer.';
  }

  get finalBenchmark(): string {
    const pricing = this.report?.pricing ?? [];
    const revised = this.value(pricing, 'Revised Benchmark PSF');
    return revised !== 'Not available' ? revised : this.value(pricing, 'Benchmark PSF');
  }

  displayLabel(label: string): string {
    const labels: Record<string, string> = {
      'Benchmark PSF': 'Base price reference (PSF)',
      'Revised Benchmark PSF': 'Final CI benchmark (PSF)',
      'Ask vs Benchmark': 'Ask vs Circled Benchmark',
      'Adjusted Current Value (UDS)': 'Estimated UDS Value',
      'UDS Support vs Close High': 'Land Value Support',
      'Close PSF - Low': 'Lower close estimate (PSF)',
      'Close PSF - High': 'Upper close estimate (PSF)',
    };
    return labels[label] ?? label;
  }

  compact(value: number | undefined): string {
    if (value == null || !Number.isFinite(value)) return 'Not available';
    const divisor = Math.abs(value) >= 10000000 ? 10000000 : Math.abs(value) >= 100000 ? 100000 : 1;
    return '₹' + (value / divisor).toLocaleString('en-IN', { maximumFractionDigits: 2 })
      + (divisor === 10000000 ? ' Cr' : divisor === 100000 ? ' L' : '');
  }

  get difference(): string {
    const value = this.report?.analysis?.difference;
    return value == null ? 'Not available' : (value > 0 ? '+' : '') + (value * 100).toFixed(1) + '%';
  }

  get savings(): string {
    const a = this.report?.analysis;
    return a?.askingPrice != null && a.closeHigh != null && a.closeLow != null
      ? this.compact(Math.max(0, a.askingPrice - a.closeHigh)) + ' - ' + this.compact(Math.max(0, a.askingPrice - a.closeLow))
      : 'Not available';
  }

}
